import {
  FontWeight,
  LiliLinkButton,
  LiliLinkType,
  LiliOtp,
  Spinner,
  SpinnerColor,
  Text,
  Heading,
} from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import { useBlocker } from '@tanstack/react-router';
import { ReactComponent as ShieldIcon } from '@Assets/icons/shield.svg';

import { useBoundStore } from '@Store/useBoundStore';
import { OnboardingFormFields } from '@Types/index';
import { convertToTimeFormat } from '@Services/utils/utils';
import { ControllerWrapper } from '@Hooks/index';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { useValidatePhoneLogic } from './hooks/useValidateUboPhoneLogic';

export const ValidatePhone = () => {
  const { t } = useTranslation(['uboOnboarding']);
  const uboData = useBoundStore((state) => state.uboData);
  const { firstName } = uboData?.secondaryCustomerDetails || {};

  useBlocker({
    shouldBlockFn: ({ next }) => {
      if (
        next.routeId === routesModel[RoutesMap.UBO_ONBOARDING_ID_VERIFICATION].path ||
        next.routeId === routesModel[RoutesMap.UBO_ONBOARDING_CREATE_PASSWORD].path
      ) {
        return false;
      }
      return true;
    },
  });

  const userName = firstName;
  const {
    handleValidateCode,
    handleSendCode,
    isLoadingValidateMfa,
    formMethods,
    isLoadingStartMfaPhoneVerification,
    timeLeft,
    otpKey,
  } = useValidatePhoneLogic();

  return (
    <FormProvider {...formMethods}>
      <div className='flex flex-col items-center  mt-[24px] md:mt-[127px] w-full max-w-[600px] gap-[24px] mx-[24px] md:mx-[0px]'>
        <div className='flex flex-col items-center gap-[24px]'>
          <ShieldIcon className='phone:hidden sm:block w-[90px] h-[90px] text-primary' />
          <div>
            <Heading.H3
              fontWeight={FontWeight.MEDIUM}
              className='phone:hidden sm:block text-center mb-[4px]'>
              {t('validatePhone.title', { firstName: userName })}
            </Heading.H3>
            <Text.H6 fontWeight={FontWeight.SEMIBOLD} className='md:hidden text-center mb-[4px]'>
              {t('validatePhone.title', { firstName: userName })}
            </Text.H6>

            <Text.H9 className='text-text-main-secondary text-center'>
              {t('validatePhone.subtitle', { phoneNumber: uboData?.mfaDestination })}
            </Text.H9>

            <Text.H9 className='text-text-main-secondary text-center'>
              {'please enter it below'}
            </Text.H9>
          </div>
          <ControllerWrapper name={OnboardingFormFields.MFA_OTP}>
            {({ error }) => (
              <>
                <LiliOtp
                  key={`${uboData?.mfaDestination}-${otpKey}`}
                  id='validate-phone-otp'
                  maxLength={6}
                  onCompleted={handleValidateCode}
                  disabled={isLoadingValidateMfa || isLoadingStartMfaPhoneVerification}
                  errorMessage={error?.message}
                  autoFocus
                  data-testid='validate-phone-otp'
                />
                {isLoadingValidateMfa && (
                  <div className='self-center -mt-[32px]'>
                    <Spinner height='24px' width='24px' spinnerColor={SpinnerColor.Blue} />
                  </div>
                )}
              </>
            )}
          </ControllerWrapper>
        </div>

        <div className='flex h-full text-center justify-center flex-col gap-[24px] mb-[40px]'>
          <div className='flex text-center justify-center flex-col gap-[8px]'>
            <Heading.H9 fontWeight={FontWeight.SEMIBOLD}>
              {t('validatePhone.noCodeReceived')}
            </Heading.H9>

            {!timeLeft && !isLoadingStartMfaPhoneVerification && (
              <LiliLinkButton
                type={LiliLinkType.QUIET}
                title={t('validatePhone.resendCode')}
                id='validate-phone-resend-code'
                onClick={handleSendCode}
                className='self-center'
              />
            )}
            {isLoadingStartMfaPhoneVerification && (
              <Spinner height='24px' width='24px' spinnerColor={SpinnerColor.Blue} />
            )}
            {timeLeft && !isLoadingStartMfaPhoneVerification && (
              <div className='flex items-center gap-[4px] self-center'>
                <Heading.H9>
                  {t('validatePhone.resendCodeTimer', { timeLeft: convertToTimeFormat(timeLeft) })}
                </Heading.H9>
              </div>
            )}
          </div>
          <Text.H10 className='text-text-main-tertiary md:px-[10px] px-[4px] md:whitespace-pre-line mt-auto'>
            {t('validatePhone.disclaimer')}
          </Text.H10>
        </div>
      </div>
    </FormProvider>
  );
};
